<script setup lang="ts">
import { useCurrencyInput } from 'vue-currency-input';
import type { ValueScaling, CurrencyDisplay } from 'vue-currency-input';

type Props = {
  modelValue?: number;
  disabled?: boolean;
  userCurrency: string;
  userLanguage: string;
  userCountry: string;
  showCurrency?: boolean;
  size?: string;
  withCents?: boolean;
};

const {
  modelValue = 0,
  disabled = false,
  userCurrency,
  userLanguage,
  userCountry,
  showCurrency = false,
  size = 'md',
  withCents = true,
} = defineProps<Props>();

const precisionValue = withCents ? 2 : 0;

const valueScalingValue = withCents
  ? ('precision' as ValueScaling)
  : ('precision' as ValueScaling);

const { inputRef, setValue } = useCurrencyInput({
  currency: userCurrency,
  locale: userLanguage,
  autoDecimalDigits: !!withCents,
  hideCurrencySymbolOnFocus: false,
  hideGroupingSeparatorOnFocus: false,
  hideNegligibleDecimalDigitsOnFocus: false,
  precision: precisionValue,
  valueScaling: valueScalingValue,
  valueRange: { min: 0 },
  currencyDisplay: 'narrowSymbol' as CurrencyDisplay,
});

watch(
  () => modelValue,
  (value) => {
    setValue(value);
    setTimeout(() => {
      if (value === null) {
        setValue(0);
      }
    }, 1);
  },
);
</script>

<template>
  <div :class="$style.inputContainer">
    <input
      ref="inputRef"
      :class="[$style.inputCurrency, { [$style.lg]: size === 'lg' }]"
      density="compact"
      :disabled="disabled"
      placeholder="R$ 0,00"
      type="text"
      variant="solo"
    />
    <DepositCurrencyButton
      v-if="showCurrency"
      :class="[$style.currencyButton, { [$style.lg]: size === 'lg' }]"
      :user-country="userCountry"
      :user-currency="userCurrency"
      :user-language="userLanguage"
    />
  </div>
</template>

<style module lang="postcss">
.inputContainer {
  @apply w-full relative;

  .inputCurrency {
    @apply h-8 px-4 w-full text-sm border-none rounded-lg placeholder-white bg-auth-bg-inputs text-auth-text-inputs focus:border-transparent focus:outline-none focus:ring-0;

    &.lg {
      @apply h-12;
    }

    &[disabled]:not([disabled='false']) {
      @apply opacity-40 cursor-not-allowed;
    }
  }

  .currencyButton {
    @apply absolute top-1/2 -translate-y-1/2 right-3 w-auto h-full;

    &.lg {
      @apply right-5;
    }
  }
}
</style>
